import withUser from '@/withUser';
import FrontLayout from '@layouts/FrontLayout';
import Head from 'next/head';
import Header from '@components/Header';
import React, { ReactNode } from 'react';
import UIWidth from '@components/presentational/UIWidth';
import Breadcrumb, { BreadcrumbItem } from '@components/app/Breadcrumb';
import {
  Box,
  Heading,
  Icon,
  Link,
  List,
  ListItem,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import {
  PiArrowSquareOutThin,
  PiFileDocThin,
  PiFilePdfThin,
} from 'react-icons/pi';
import BgMasqueHaut from '@/components/presentational/img/v2024/BgMasqueHaut.svg';

const MyLink = ({ href, children }: { href: string; children: ReactNode }) => {
  return (
    <Link
      href={href}
      as={'a'}
      display="inline-flex"
      textDecoration="underline"
      _hover={{
        textDecoration: 'none',
      }}
      isExternal
    >
      {children} <Icon as={PiArrowSquareOutThin} ml=".25rem" />
    </Link>
  );
};

function PageConditionsGeneralesDUtilisation() {
  return (
    <FrontLayout
      gtmPage={{
        page_type: 'Commun',
        page_level1: "Conditions Générales d'Utilisation",
      }}
    >
      <Head>
        <title>Conditions Générales d'Utilisation | IFprofs</title>
      </Head>
      <Header />
      <Breadcrumb
        breadcrumbItems={[
          { label: 'Accueil', href: '/' },
          { label: "Conditions Générales d'Utilisation" },
        ]}
      >
        <BreadcrumbItem href="/" label="Accueil" />
        <BreadcrumbItem label="Conditions Générales d'Utilisation" />
      </Breadcrumb>
      <Box
        as="section"
        data-name="kit-de-comminication"
        pb={{ base: '2.5rem', md: '3.5rem' }}
        backgroundImage={{ sm: `url("${BgMasqueHaut.src}")` }}
        backgroundRepeat={{ sm: 'no-repeat' }}
        backgroundPosition={{ sm: '0 -30rem' }}
        backgroundSize={{ sm: '512rem auto' }}
        backgroundColor={{ sm: 'brand.10' }}
      >
        <UIWidth>
          <Heading as="h1" variant="h1" mb="1em">
            Conditions Générales d'Utilisation
          </Heading>
          <Text my="1em" fontSize="2rem">
            <strong>Mes engagements en tant que membre IFprofs&nbsp;:</strong>
          </Text>
          <List mb="3rem">
            <ListItem>
              <Heading as="h2" variant="h3" mt="2em">
                1. Je suis responsable des contenus que je publie sur IFprofs.
              </Heading>
              <Box ml="1.5rem">
                <Text my="1em">
                  Je publie uniquement des&nbsp;
                  <strong>contenus d’ordre professionnel</strong>&nbsp;et&nbsp;
                  <strong>liés au domaine de l’éducation francophone.</strong>
                </Text>
                <Text my="1em">
                  Je publie<strong> uniquement des contenus </strong>(textes,
                  images, photos, vidéos, son, animations)&nbsp;:
                </Text>
                <UnorderedList ml="3em">
                  <ListItem>
                    <strong>dont je suis l’auteur</strong>
                  </ListItem>
                  <ListItem>
                    <strong>
                      que l’auteur initial m’a autorisé à mettre en ligne sur
                      IFprofs
                    </strong>
                  </ListItem>
                  <ListItem>
                    <strong>
                      <MyLink href="https://creativecommons.org/">
                        sous licence libre
                      </MyLink>
                    </strong>{' '}
                    (site en anglais)
                  </ListItem>
                </UnorderedList>
                <Text my="1em">
                  "Exception pédagogique" : je peux également publier des&nbsp;
                  <strong>extraits d’œuvres</strong>&nbsp;(sauf d’une œuvre
                  conçue à des fins pédagogiques).
                </Text>
                <Text my="1em">
                  Lorsque je publie un contenu dont je ne suis pas
                  l’auteur,&nbsp;j’indique<strong> le prénom et le nom</strong>
                  &nbsp;de son auteur ainsi que sa&nbsp;<strong>source</strong>.
                  &nbsp;
                </Text>
                <Text my="1em">
                  Mes publications sont <strong>en français</strong>, pour
                  pouvoir être comprises par&nbsp;les membres du monde entier.
                </Text>
                <Text my="1em">
                  Je ne publie
                  <strong>
                    {' '}
                    pas de contenu pouvant porter atteinte à autrui
                  </strong>
                  &nbsp;(propos diffamant, injurieux, dénigrant, malveillant,
                  obscène, incitant à la violence et à la haine…).
                </Text>
                <Text my="1em">
                  Mes publications ne contiennent aucun élément conçu pour
                  limiter ou détruire le fonctionnement des équipements
                  informatiques (virus par exemple).
                </Text>
                <Text my="1em">
                  <em>
                    Le fonctionnement du réseau social&nbsp;IFprofs repose sur
                    la participation de ses membres. Ce que vous publiez
                    sur&nbsp;IFprofs&nbsp;est instantanément et directement mis
                    en ligne.
                  </em>
                </Text>
                <Text my="1em">
                  <em>
                    Dans la loi française, qui s’applique sur&nbsp;IFprofs,
                    l'auteur d’un contenu est le&nbsp;propriétaire de ce contenu
                    et est responsable de sa diffusion.
                  </em>
                </Text>
              </Box>
            </ListItem>
            <ListItem>
              <Heading as="h2" variant="h3" mt="2em">
                2. Je suis responsable de mon profil.
              </Heading>
              <Box ml="1.5rem">
                <Text my="1em">
                  Je crée&nbsp;<strong>un seul profil </strong>à mon nom.
                </Text>
                <Text my="1em">
                  A tout moment,&nbsp;
                  <strong>je peux consulter et modifier</strong>
                  &nbsp;les informations que j’ai saisies sur mon profil ou
                  demander aux animateurs de supprimer mon profil.
                </Text>
                <Text my="1em">
                  Je ne créé pas de profil en me faisant passer pour quelqu’un
                  d’autre sans son accord.
                </Text>
                <Text my="1em">
                  <em>
                    Le traitement des informations obligatoires renseignées lors
                    de la création du profil a&nbsp;fait l’objet d’une
                    déclaration à la Commission nationale de l’informatique et
                    des libertés (
                    <MyLink href="http://www.cnil.fr/">CNIL</MyLink>
                    ).&nbsp;
                  </em>
                </Text>
                <Text my="1em">
                  <em>
                    Seuls les animateurs peuvent accéder à l’adresse
                    électronique que vous utilisez pour l’identification à votre
                    profil. Cette adresse est&nbsp;maintenue confidentielle.
                  </em>
                </Text>
              </Box>
            </ListItem>
            <ListItem>
              <Heading as="h2" variant="h3" mt="2em">
                3. J’utilise les publications des autres membres sous certaines
                conditions.
              </Heading>
              <Box ml="3rem">
                <Text my="1em">
                  Sur IFprofs, je peux utiliser les publications d’autres
                  membres comme il me convient. Je peux ainsi commenter la
                  publication, la lier à l’une de mes publications, la
                  sélectionner, etc.
                </Text>
                <Text my="1em">
                  <strong>En dehors de la navigation sur IFprofs&nbsp;:</strong>
                </Text>
                <UnorderedList ml="3em">
                  <ListItem>
                    Si j’utilise les publications d’autres membres dans un cadre
                    professionnel (en classe, pour des professionnels de
                    l’éducation…)&nbsp;: j’indique le prénom et le nom de
                    l’auteur.
                  </ListItem>
                  <ListItem>
                    Si j’utilise les publications des membres auprès d'un public
                    autre que les élèves, les étudiants et les professionnels de
                    l'éducation&nbsp;: je demande l’autorisation à l’auteur.
                  </ListItem>
                </UnorderedList>
                <Text my="1em">
                  Dans tous les cas,<strong>&nbsp;</strong>je n’ai{' '}
                  <strong>pas le droit </strong>de copier ni&nbsp;de
                  diffuser&nbsp;le contenu d’IFprofs à des&nbsp;
                  <strong>fins commerciales.</strong>
                </Text>
                <Text my="1em">
                  <em>
                    Si vous utilisez le contenu d’un autre membre, pensez à lui
                    faire un retour, en laissant un commentaire sur IFprofs par
                    exemple.
                  </em>
                </Text>
              </Box>
            </ListItem>
            <ListItem>
              <Heading as="h2" variant="h3" mt="2em">
                4. Je préviens les animateurs IFprofs si je repère une anomalie.
              </Heading>
              <Box ml="1em">
                <Text my="1em">
                  Une publication n’est pas d’ordre professionnel ou ne figure
                  pas dans la bonne rubrique ? Un membre a publié un propos
                  malveillant&nbsp;? &nbsp;
                  <strong>
                    Je le signale le plus vite possible aux animateurs&nbsp;
                  </strong>
                  (contacts accessibles en cliquant sur la&nbsp;photo de profil
                  en haut à droite de l’écran).
                </Text>
              </Box>
            </ListItem>
          </List>
        </UIWidth>
      </Box>
    </FrontLayout>
  );
}

export default withUser(PageConditionsGeneralesDUtilisation);
